import Root from './components/Root'
import NotFound from './NotFound'
import { Applications, ApplicationView } from 'components/Applications'
import { FlowEditor, NewFlow, Flows } from 'components/Applications/Flows'
import { Field } from 'components/Applications/Fields'
import { Dashboard } from 'components/Dashboard'
import { Users, User, UserPreferences } from 'components/Users'
import { Groups, Group } from 'components/Groups'
import { Settings, Setting } from 'components/ManateeSettings'
import { Restrictions, Restriction } from 'components/FlowRestrictions'
import { Superflows } from 'components/Superflows'
import {
  Configuration,
  Subjects,
  Subject,
  Manufacturers,
  Manufacturer
} from 'components/Configuration'
import { Hubs, Hub, DownloadFlow, HubHandler } from 'components/Hub'
import { Home } from 'components/Home'
import { Login, Logout } from 'components/Account'
import { Tables, Table, NewTable, Localization } from 'components/Tables'
import { Services, Service } from 'components/Services'
import { Estero, EsteroListing, EsteroDetail, Alerts, Alert } from 'components/Estero'
import _ from 'lodash'

const routes = [
  { name: 'root', path: '/', component: Root },
  { name: 'home', path: '/home', component: Home },
  {
    name: 'applications',
    path: '/applications',
    component: Applications,
    children: [
      {
        path: 'app/:appId/settings/:modeArg?',
        name: 'app-settings',
        component: ApplicationView,
        props: route => _.assign({ entityId: route.params.appId }, route.params)
      },
      {
        path: 'app/new',
        name: 'new-app',
        component: ApplicationView
      },
      {
        path: 'app/:appId/flow/new',
        name: 'new-flow',
        component: NewFlow,
        props: true
      },
      {
        path: 'app/:appId/flow/download',
        name: 'download-flow',
        component: DownloadFlow,
        props: true
      },
      {
        path: 'app/:appId/flow/:flowId/mode/:mode/:modeArg?',
        name: 'app-flow',
        component: FlowEditor,
        props: route => _.assign({ entityId: route.params.flowId }, route.params),
        meta: {
          shortcuts: [
            { shortcutUniversal: '^ B', action: 'SHORTCUT_BEAUTIFY' },
            {
              shortcutUniversal: 'F1',
              action: 'SHORTCUT_SHOW_COMMAND_PALETTE'
            },
            {
              shortcutWin: '^ F',
              shortcutMac: '⌘ F',
              action: 'SHORTCUT_START_SEARCHING'
            },
            {
              shortcutWin: '^ G',
              shortcutMac: '⌘ G',
              action: 'SHORTCUT_FIND_NEXT'
            },
            {
              shortcutWin: '⇧ ⌃ G',
              shortcutMac: '⇧ ⌘ G',
              action: 'SHORTCUT_FIND_PREVIOUS'
            },
            {
              shortcutWin: '^ F',
              shortcutMac: '⌥ ⌘ F',
              action: 'SHORTCUT_REPLACE'
            },
            {
              shortcutWin: '^ D',
              shortcutMac: '⌘ D',
              action: 'SHORTCUT_ADD_SELECTION'
            },
            { shortcutUniversal: '⌃ ␣', action: 'SHORTCUT_AUTOCOMPLETE' },
            {
              shortcutUniversal: 'F10',
              action: 'SHORTCUT_RUN_FLOW'
            },
            {
              shortcutUniversal: 'F11',
              action: 'SHORTCUT_DEBUG_FLOW'
            }
          ]
        }
      },
      {
        path: 'app/:appId/field/new/:tabIndex?',
        name: 'new-field',
        component: Field,
        props: route =>
          _.assign(
            {
              urlTabIndex: parseInt(route.params.tabIndex || '0', 10)
            },
            route.params
          )
      },
      {
        path: 'app/:appId/field/:fieldId/:tabIndex?',
        name: 'app-field',
        component: Field,
        props: route =>
          _.assign(
            {
              entityId: route.params.fieldId,
              urlTabIndex: parseInt(route.params.tabIndex || '0', 10)
            },
            route.params
          )
      }
    ]
  },
  {
    name: 'flows',
    path: '/flows',
    component: Flows,
    props: true,
    children: [
      {
        path: 'grouped-by/:groupBy/matching/:filterWith/showing/:columns/sort-by/:sortBy',
        name: 'flows-custom',
        component: Flows,
        props: true
      },
      {
        path: 'saved-as/:selectedSearch',
        name: 'flows-saved',
        component: Flows,
        props: true
      }
    ]
  },
  {
    name: 'users',
    path: '/users',
    component: Users,
    children: [
      {
        path: 'user/new',
        name: 'new-user',
        component: User
      },
      {
        path: 'user/:userId',
        name: 'user',
        component: User,
        props: route => _.assign({ entityId: route.params.userId }, route.params)
      }
    ]
  },
  {
    name: 'groups',
    path: '/groups',
    component: Groups,
    children: [
      {
        path: 'group/new',
        name: 'new-group',
        component: Group
      },
      {
        path: 'group/:groupId',
        name: 'group',
        component: Group,
        props: route => _.assign({ entityId: route.params.groupId }, route.params)
      }
    ]
  },
  {
    name: 'settings',
    path: '/settings',
    component: Settings,
    children: [
      {
        path: 'new',
        name: 'new-setting',
        component: Setting
      },
      {
        path: 'setting/:settingId',
        name: 'setting',
        component: Setting,
        props: route => _.assign({ entityId: route.params.settingId }, route.params)
      }
    ]
  },
  {
    name: 'restrictions',
    path: '/restrictions',
    component: Restrictions,
    children: [
      {
        path: 'new',
        name: 'new-restriction',
        component: Restriction
      },
      {
        path: 'restriction/:restrictionId',
        name: 'restriction',
        component: Restriction,
        props: route => _.assign({ entityId: route.params.restrictionId }, route.params)
      }
    ]
  },
  {
    name: 'estero',
    path: '/estero',
    component: Estero,
    // LQ== is '-'
    redirect: 'estero/as/novice/with-code/LQ==/sort-by/-/at/0',
    props: true,
    children: [
      {
        path: 'as/:mode',
        name: 'estero-as',
        component: EsteroListing,
        props: true,
        children: [
          {
            path: 'with-id/:viewId/sort-by/:sortByEnc/at/:atPage',
            name: 'estero-with-view-id',
            component: Estero,
            props: true
          },
          {
            path: 'with-code/:code/sort-by/:sortByEnc/at/:atPage',
            name: 'estero-with-view-code',
            component: Estero,
            props: true
          },
          {
            path: 'with-code/-/sort-by/:sortByEnc/at/:atPage',
            name: 'estero-with-all',
            component: Estero,
            props: true
          }
        ]
      },
      { path: 'detail/:id', name: 'estero-details', component: EsteroDetail, props: true }
    ]
  },
  {
    name: 'i18n',
    path: '/i18n',
    component: Localization,
    redirect: '/i18n/for/cuesta',
    props: true,
    children: [
      {
        name: 'i18n-for',
        path: 'for/:target',
        component: Localization,
        props: true
      }
    ]
  },
  {
    name: 'tables',
    path: '/tables',
    component: Tables,
    children: [
      {
        path: 'table/new',
        name: 'new-table',
        component: NewTable
      },
      {
        path: 'table/:tableId',
        name: 'table-home',
        component: Table,
        redirect: 'table/:tableId/data',
        props: route => _.assign({ entityId: route.params.tableId }, route.params),
        children: [
          {
            path: ':mode',
            name: 'table',
            component: Table,
            props: true,
            meta: {
              shortcuts: [
                { shortcutUniversal: '^ ⇧ A', action: 'TABLE_INSERT_ROW' },
                { shortcutUniversal: '^ ⇧ ⌫', action: 'TABLE_DELETE_ROW' },
                { shortcutUniversal: '⎇ ⬅', action: 'TABLE_MOVE_CELL_LEFT' },
                { shortcutUniversal: '⎇ ➡', action: 'TABLE_MOVE_CELL_RIGHT' },
                { shortcutUniversal: '⎇ ⬆', action: 'TABLE_MOVE_ROW_UP' },
                { shortcutUniversal: '⎇ ⬇', action: 'TABLE_MOVE_ROW_DOWN' },
                { shortcutUniversal: '^ X', action: 'TABLE_SELECT_ROW' }
              ]
            }
          }
        ]
      }
    ]
  },
  {
    name: 'services',
    path: '/services/:serviceType',
    component: Services,
    props: true,
    children: [
      {
        path: 'new',
        name: 'new-service',
        component: Service,
        props: true
      },
      {
        path: ':serviceId',
        name: 'service',
        props: route => _.assign({ entityId: route.params.serviceId }, route.params),
        component: Service
      }
    ]
  },
  {
    path: '/web+hubinstall:/:origin/:type/:identifier',
    name: 'install',
    component: HubHandler,
    props: true
  },
  { name: 'superflows', path: '/superflows', component: Superflows },
  { name: 'dashboard', path: '/dashboard', component: Dashboard },
  {
    name: 'configuration',
    path: '/configuration',
    component: Configuration,
    children: [
      {
        path: 'preferences',
        name: 'preferences',
        component: UserPreferences,
        props: route => _.assign({ entityId: 'logged-in-user' }, route.params)
      },
      {
        path: 'subjects',
        name: 'subjects',
        component: Subjects,
        children: [
          {
            path: 'subject/new',
            name: 'new-subject',
            component: Subject
          },
          {
            path: 'subject/:subjectId',
            name: 'subject',
            component: Subject,
            props: route => _.assign({ entityId: route.params.subjectId }, route.params)
          }
        ]
      },
      {
        path: 'manufacturers',
        name: 'manufacturers',
        component: Manufacturers,
        children: [
          {
            path: 'manufacturer/new',
            name: 'new-manufacturer',
            component: Manufacturer
          },
          {
            path: 'manufacturer/:manufacturerId',
            name: 'manufacturer',
            component: Manufacturer,
            props: route => _.assign({ entityId: route.params.manufacturerId }, route.params)
          }
        ]
      },
      {
        path: 'hubs',
        name: 'hubs',
        component: Hubs,
        children: [
          {
            path: 'hub/new',
            name: 'new-hub',
            component: Hub
          },
          {
            path: 'hub/:hubId/at/:tab',
            name: 'hub',
            component: Hub,
            props: route =>
              _.assign({ entityId: route.params.hubId, tab: route.params.tab }, route.params)
          }
        ]
      }
    ]
  },
  {
    name: 'alerts',
    path: '/alerts',
    component: Alerts,
    children: [
      {
        path: 'alert/new',
        name: 'new-alert',
        component: Alert
      },
      {
        path: 'alert/:alertId',
        name: 'alert',
        component: Alert,
        props: route => _.assign({ entityId: route.params.alertId }, route.params)
      }
    ]
  },
  {
    name: 'login',
    path: '/login',
    component: Login,
    props: true
  },
  { name: 'logout', path: '/logout', component: Logout, props: true },
  { name: 'not-found', path: '*', component: NotFound }
]

export default routes
